import React, { createContext, useState, useContext, useEffect } from 'react';
import { authApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const clearAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    authApi.setAuthToken(null);
  };

  const validateToken = async (token) => {
    try {
      authApi.setAuthToken(token);
      const response = await authApi.getCurrentUser();
      return response.success;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          
          // Validate token by making a test request
          const isValid = await validateToken(token);
          
          if (isValid) {
            setUser(parsedUser);
            setIsAuthenticated(true);
            authApi.setAuthToken(token);
            console.log('Token validated successfully');
          } else {
            console.log('Token invalid, clearing auth');
            clearAuth();
          }
        } catch (error) {
          console.error('Error initializing auth:', error);
          clearAuth();
        }
      } else {
        clearAuth(); // Ensure clean state if no token
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await authApi.login({ username, password });
      
      if (response.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setUser(response.data.user);
        setIsAuthenticated(true);
        authApi.setAuthToken(response.data.token);
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'An error occurred' };
    }
  };

  const logout = () => {
    clearAuth();
    navigate('/login');
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    clearAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};